import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { db } from "@/lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

const submissionSchema = z.object({
  lenderUid: z.string().min(1, "Lender ID is required"),
  product: z.enum(["term_loan", "revolver", "line_of_credit", "equipment_financing"]),
  rate: z.number().min(0).max(100),
  termMonths: z.number().min(1).max(360),
  maxAmount: z.number().min(0),
  conditions: z.string(),
  dealId: z.string().min(1, "Deal ID is required"),
});

type SubmissionFormData = z.infer<typeof submissionSchema>;

export default function SeedSubmissions() {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<SubmissionFormData>({
    resolver: zodResolver(submissionSchema),
    defaultValues: {
      lenderUid: "",
      product: "term_loan",
      rate: 8.0,
      termMonths: 60,
      maxAmount: 500000,
      conditions: "",
      dealId: "",
    },
  });

  const onSubmit = async (data: SubmissionFormData) => {
    setLoading(true);
    try {
      const conditionsArray = data.conditions
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c.length > 0);

      await addDoc(collection(db, `lenders/${data.lenderUid}/submissions`), {
        product: data.product,
        rate: data.rate,
        termMonths: data.termMonths,
        maxAmount: data.maxAmount,
        conditions: conditionsArray,
        createdAt: serverTimestamp(),
        status: "to_review",
        dealId: data.dealId,
      });

      toast({
        title: "Success",
        description: "Lender submission created successfully",
      });

      form.reset();
    } catch (error) {
      console.error("Error creating submission:", error);
      toast({
        title: "Error",
        description: "Failed to create submission",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const seedMultiple = async () => {
    const lenderUid = form.getValues("lenderUid");
    if (!lenderUid) {
      toast({
        title: "Error",
        description: "Please enter a Lender ID first",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const base = collection(db, `lenders/${lenderUid}/submissions`);

      await addDoc(base, {
        product: "term_loan",
        rate: 8.2,
        termMonths: 60,
        maxAmount: 750000,
        conditions: ["PG required", "Min DSCR 1.25"],
        createdAt: serverTimestamp(),
        status: "to_review",
        dealId: "DEAL001",
      });

      await addDoc(base, {
        product: "revolver",
        rate: 7.5,
        termMonths: 24,
        maxAmount: 250000,
        conditions: ["ABL", "Borrowing base monthly"],
        createdAt: serverTimestamp(),
        status: "to_review",
        dealId: "DEAL002",
      });

      await addDoc(base, {
        product: "line_of_credit",
        rate: 9.0,
        termMonths: 12,
        maxAmount: 100000,
        conditions: ["Personal guarantee", "Monthly reporting"],
        createdAt: serverTimestamp(),
        status: "to_review",
        dealId: "DEAL003",
      });

      toast({
        title: "Success",
        description: "3 sample submissions created successfully",
      });
    } catch (error) {
      console.error("Error seeding submissions:", error);
      toast({
        title: "Error",
        description: "Failed to seed submissions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Seed Lender Submissions</CardTitle>
          <CardDescription>
            Create test lender submissions for development and testing purposes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="lenderUid"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lender ID</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter lender UID" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dealId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Deal ID</FormLabel>
                    <FormControl>
                      <Input placeholder="DEAL001" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="product"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Product Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select product type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="term_loan">Term Loan</SelectItem>
                        <SelectItem value="revolver">Revolver</SelectItem>
                        <SelectItem value="line_of_credit">Line of Credit</SelectItem>
                        <SelectItem value="equipment_financing">Equipment Financing</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Interest Rate (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.1"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="termMonths"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term (Months)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="maxAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximum Amount ($)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="conditions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Conditions (comma-separated)</FormLabel>
                    <FormControl>
                      <Input placeholder="PG required, Min DSCR 1.25" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Single Submission
                </Button>
                <Button type="button" variant="secondary" onClick={seedMultiple} disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Seed 3 Sample Submissions
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
